import React, { forwardRef, useMemo } from 'react'
import { lightTheme } from '@/customTheme'
import { StyleObject } from 'styletron-react'
import { Z_INDEX } from '@/constants/z-index'
import { 
  FloatingInspectorButton, 
  FloatingInspectorButtonHandle,
  FloatingInspectorButtonArrow,
  IconWrapper,
  MainText, 
  PopoverContentType
} from '../../FloatingInspectorButton/FloatingInspectorButton'

const ghostPulseKeyframes = {
  from: {
    background: lightTheme.colors.grayScale150,
  },
  '50%': {
    background: lightTheme.colors.grayScale100,
  },
  to: {
    background: lightTheme.colors.grayScale150,
  },
}

interface RoundedButtonProps {
  icon?: React.ReactNode
  text?: string
  state?: 'default' | 'ghost'
  width?: number
  onClick?: () => void
  disabled?: boolean
  id?: string
  tooltipText?: string
  popoverContent?: PopoverContentType
  showArrow?: FloatingInspectorButtonArrow
  iconSize?: number
  styleOverride?: StyleObject
}

const RoundedButton = forwardRef<FloatingInspectorButtonHandle, RoundedButtonProps>(
  ({ icon, text, state = 'default', width, onClick, disabled, id, tooltipText, popoverContent, showArrow, iconSize, styleOverride }, ref) => {
    const isGhost = state === 'ghost'
    const defaultStyles = useMemo<StyleObject>(
      () => ({
        boxShadow: '0px 0px 6px 0px rgba(0, 0, 0, 0.08)',
        transitionProperty: 'box-shadow',
        transitionDuration: '0.2s',
        transitionTimingFunction: 'ease',
        transitionDelay: '0.3s',
        ':hover': {
          background: lightTheme.colors.white,
          boxShadow: '0px 0px 6px rgba(0, 0, 0, 0.08), 0px 0px 10px rgba(2, 252, 255, 0.30), 0px 4px 10px rgba(244, 9, 255, 0.15)',
          transitionDelay: '0s',
          cursor: 'pointer',
        },
        ':active': {
          background: lightTheme.colors.white,
          boxShadow: '0px 0px 6px rgba(0, 0, 0, 0.08), 0px -1px 10px rgba(2, 252, 255, 0.60), 0px 4px 10px rgba(244, 9, 255, 0.4)',
          transitionDelay: '0s',
        },
        '[data-selected="true"]': {
          background: lightTheme.colors.white,
          boxShadow: '0px 0px 6px rgba(0, 0, 0, 0.08), 0px -1px 10px rgba(2, 252, 255, 0.60), 0px 4px 10px rgba(244, 9, 255, 0.4)',
          transitionDelay: '0s',
        },
      }),
      []
    )
    const ghostStyles = useMemo<StyleObject>(
      () => ({
        minWidth: '111px',
        background: lightTheme.colors.grayScale150,
        boxShadow: 'none',
        animationName: ghostPulseKeyframes,
        animationDuration: '1.4s',
        animationDelay: '0.3s',
        animationIterationCount: 'infinite',
        animationTimingFunction: 'linear',
      }),
      []
    )
    
    return (
      <FloatingInspectorButton
        ref={ref}
        onClick={onClick || (() => {})}
        disabled={disabled}
        id={id}
        borderRadiusStyle="rounded"
        nonInteractive={isGhost}
        tooltipText={tooltipText}
        showArrow={showArrow}
        arrowVariant="tight"
        popoverContent={popoverContent}
        tooltipOptions={{
          design: 'compact',
          maxWidth: 158,
          textAlign: 'center',
          ignoreBoundary: true,
          zIndex: Z_INDEX.FLOATING_INSPECTOR_POPOVER + 1,
        }}
        placementTooltip="top"
        style={{
          height: '36px',
          maxWidth: '220px',
          width: width ? `${width}px` : 'fit-content',
          padding: '6px 12px 6px 8px',
          gap: '4px',
          flexShrink: 0,
          background: lightTheme.colors.white,
          ...lightTheme.typography.Small12regular,
          color: lightTheme.colors.blackGray,
          ...(isGhost ? ghostStyles : defaultStyles) as any,
          ...styleOverride,
        }}
      >
        {icon && (
          <IconWrapper $style={iconSize ? { width: `${iconSize}px`, height: `${iconSize}px` } : undefined}>
            {icon}
          </IconWrapper>
        )}
        {text && (
          <MainText
            $truncate
            $fontWeight={400}
            style={{ ...lightTheme.typography.Small12regular }}
          >
            {text}
          </MainText>
        )}
      </FloatingInspectorButton>
    )
  }
)

export default RoundedButton
